import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Money, Search } from '@mui/icons-material';
import { apiService } from '../services/api';

const GoodFaithPaymentPage: React.FC = () => {
  const [recipientType, setRecipientType] = useState<'customer' | 'provider'>('customer');
  const [recipientId, setRecipientId] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<any | null>(null);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [processStripePayment, setProcessStripePayment] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSearch = async () => {
    if (!recipientSearch.trim()) {
      setError('Please enter an email or name to search');
      return;
    }

    try {
      setSearching(true);
      setError('');
      const response = await apiService.get(`/admin/users/search?q=${encodeURIComponent(recipientSearch)}&userType=${recipientType}`);
      
      if (response.success && response.data) {
        // Ensure response.data is an array
        const searchData = Array.isArray(response.data) ? response.data : [];
        setSearchResults(searchData);
        if (searchData.length === 0) {
          setError('No users found');
        }
      } else {
        setError('Failed to search users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleRecipientSelect = (recipient: any) => {
    setSelectedRecipient(recipient);
    setRecipientId(recipient._id || recipient.id);
  };

  const handleSubmit = async () => {
    if (!recipientId) {
      setError('Please select a recipient');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!reason.trim()) {
      setError('Please enter a reason (required for audit purposes)');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');
      const response = await apiService.post('/admin/goodwill-payment', {
        recipientType,
        recipientId,
        amount: parseFloat(amount),
        reason: reason.trim(),
        notes: notes.trim(),
        processStripePayment,
      });

      if (response.success) {
        setSuccess(`Good faith ${recipientType === 'customer' ? 'refund' : 'payment'} processed successfully!`);
        // Reset form
        setRecipientId('');
        setRecipientSearch('');
        setSearchResults([]);
        setSelectedRecipient(null);
        setAmount('');
        setReason('');
        setNotes('');
      } else {
        setError(response.error || 'Failed to process good faith payment');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to process good faith payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Good Faith Refund/Payment
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Process good faith refunds to customers or payments to providers directly through Stripe. All transactions are tracked for audit and tax purposes.
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Customer Refunds:</strong> Requires an original payment to refund. The system will find the most recent payment and process the refund.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Provider Payments:</strong> Payments are transferred directly to the provider's Stripe Connect account. All transactions are logged for audit purposes.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Recipient Type</InputLabel>
          <Select
            value={recipientType}
            onChange={(e) => {
              setRecipientType(e.target.value as any);
              setRecipientId('');
              setSelectedRecipient(null);
              setSearchResults([]);
              setRecipientSearch('');
            }}
            label="Recipient Type"
          >
            <MenuItem value="customer">Customer (Refund)</MenuItem>
            <MenuItem value="provider">Provider (Payment)</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Search for {recipientType === 'customer' ? 'customer' : 'provider'} by email or name
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Search"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder={`Enter ${recipientType === 'customer' ? 'customer' : 'provider'} email or name`}
            />
            <Button
              variant="contained"
              startIcon={searching ? <CircularProgress size={20} /> : <Search />}
              onClick={handleSearch}
              disabled={searching || !recipientSearch.trim()}
            >
              Search
            </Button>
          </Box>
        </Box>

        {searchResults.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select {recipientType === 'customer' ? 'customer' : 'provider'}:
            </Typography>
            <Autocomplete
              options={searchResults}
              getOptionLabel={(option) => {
                if (recipientType === 'customer') {
                  return `${option.firstName || ''} ${option.lastName || ''} (${option.email || ''})`.trim();
                } else {
                  return `${option.businessName || `${option.firstName || ''} ${option.lastName || ''}`.trim()} (${option.email || ''})`.trim();
                }
              }}
              value={selectedRecipient}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleRecipientSelect(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Selected ${recipientType === 'customer' ? 'Customer' : 'Provider'}`}
                  placeholder="Select from search results"
                />
              )}
            />
          </Box>
        )}

        <TextField
          fullWidth
          label="Amount"
          type="text"
          value={amount}
          onChange={(e) => {
            // Allow only numbers and decimal point
            const value = e.target.value;
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              setAmount(value);
            }
          }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
          }}
          helperText="Enter amount in dollars (e.g., 50.00)"
        />

        <TextField
          fullWidth
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          sx={{ mb: 2 }}
          helperText="Required: Reason for this good faith payment/refund (for audit purposes)"
          multiline
          rows={2}
        />

        <TextField
          fullWidth
          label="Internal Notes (Optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Internal notes (not visible to recipient)"
          multiline
          rows={2}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRecipientId('');
              setSelectedRecipient(null);
              setSearchResults([]);
              setRecipientSearch('');
              setAmount('');
              setReason('');
              setNotes('');
              setError('');
              setSuccess('');
            }}
            disabled={processing}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={processing ? <CircularProgress size={20} /> : <Money />}
            onClick={handleSubmit}
            disabled={processing || !recipientId || !amount || !reason.trim()}
          >
            {processing ? 'Processing...' : `Process ${recipientType === 'customer' ? 'Refund' : 'Payment'}`}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default GoodFaithPaymentPage;
